// frontend/src/utils/avatar.js
// Shared identity helpers: how a store or user is named, coloured and
// abbreviated wherever the app shows who you are — the colored squircle chips
// (see styles/avatar-chip.css) plus the names beside them. Used by the
// store-activity rail/hero, the top bars' identity chips, the user dropdown,
// and the phone's store and More sheets, so every surface names the same
// person the same way.

// Stable color for a seed (a store/user id). Same seed → same color forever.
// Hashes to a hue on a 360-point wheel (fixed saturation/lightness tuned for
// white text on both light and dark surfaces) rather than a small fixed
// palette — a handful of stores would otherwise collide on the same swatch.
export function avatarColor(seed = '') {
  const s = String(seed ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 58%, 48%)`;
}

// How a signed-in user is named on screen. One home, because the chip label,
// the chip's initials and the dropdown/mobile headers must never disagree
// about who is signed in.
//
// The API always supplies this: UserSerializer.get_full_name returns
// "first last" when either name part is set and the email otherwise
// (backend/authentication/serializers.py), and User.email is unique, required
// and the USERNAME_FIELD (backend/authentication/models.py). A user payload
// without a name is therefore not a real user — raise rather than substitute a
// placeholder, which would paint a fabricated identity next to real store data.
export function userDisplayName(user) {
  const name = user?.full_name?.trim();
  if (!name) {
    throw new Error('userDisplayName: user payload has no full_name');
  }
  return name;
}

// True when the email adds information the display name doesn't already carry.
// get_full_name returns the email itself for a user who never set a first or
// last name, so rendering both unconditionally prints it twice.
export function userEmailIsDistinct(user) {
  const name = userDisplayName(user);
  return Boolean(user.email) && user.email !== name;
}

// Seed the user chip on the immutable id, the way every store chip seeds on
// store.id. Email is the login credential and users can change it, which would
// repaint their chip a new colour and break the same-seed-same-colour-forever
// rule above.
export function userAvatarSeed(user) {
  const id = user?.id;
  if (id === undefined || id === null || id === '') {
    throw new Error('userAvatarSeed: user payload has no id');
  }
  return String(id);
}

// How a store is named on screen. Unlike a user, a store has no single
// guaranteed name field: Store.name is blank=True and Store.domain is
// null=True/blank=True — "NULL for manual stores" (backend/core/models.py) —
// so a store carrying only a domain, or only an id, is valid data rather than
// a broken payload. Each tier below is a real identifier the merchant can act
// on, never a placeholder, and the id is always there because it is the PK.
export function storeDisplayName(store) {
  const name = store?.name?.trim();
  if (name) return name;
  const domain = store?.domain?.trim();
  if (domain) return domain;
  const id = store?.id;
  if (id === undefined || id === null || id === '') {
    throw new Error('storeDisplayName: store payload has no name, domain or id');
  }
  return String(id);
}

// True when the domain adds information the store's display name doesn't
// already carry. Every surface that shows a store draws its name with the
// domain underneath; for a store with no name of its own the two lines would
// otherwise print the same domain twice.
export function storeDomainIsDistinct(store) {
  const name = storeDisplayName(store);
  const domain = store.domain?.trim();
  return Boolean(domain) && domain !== name;
}

// Who the top bar's identity chip shows: the store the session is on, or the
// user while no store is selected, so the bar always has an identity. One
// call, so the label, its initials and its colour never disagree about which
// of the two it is.
export function sessionIdentity(user, store) {
  return store
    ? { label: storeDisplayName(store), seed: store.id }
    : { label: userDisplayName(user), seed: userAvatarSeed(user) };
}

// Single word → first 3 letters (Shopify store-switcher convention);
// multi-word → first letter of the first two words.
export function initials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
